import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17142 — which invoices sold in which product group, origin and company
 * on each past day, so the sales reports that count invoices per category,
 * product group, manufacturer, brand, family or origin count them exactly
 * from the summary. An invoice with lines in two categories counts once in
 * each; added up per item, as the line summary is, it would count once per
 * item.
 *
 * One row per day, invoice and distinct combination of the line attributes
 * those reports group by: the item's category, product group code
 * (`customFields.productGroupCode`), manufacturer, brand and family, whether
 * the item exists, is deleted or is active, and the line's order origin,
 * over the lines that count toward sales (`excludeFromSales = 0`), not
 * deleted, of invoices not deleted. The invoice's customer, rep, company,
 * type, cancelled and has-sales flags ride along for the reports' filters.
 * About two rows per invoice on Macomb, against about 3.5 lines.
 *
 * The item attributes are stored as they read when the day was built (the
 * reports group by text that has no id of its own). So that a later edit of
 * an item never leaves a stale group behind, `sales_summary_item_group`
 * holds each summarized item's attributes, and the refresh marks every
 * summarized day that sold an item whose attributes changed as waiting to be
 * rebuilt; reports read those days from the invoices meanwhile.
 *
 * Not audited (`auditable: false`): a derived cache rebuilt a day at a time.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('sales_summary_invoice_group_day', {}, { auditable: false })
// The grouped invoice counts: a date range, the flags and company the
// reports filter on, then the customer, rep and invoice they count.
@Index('sales_summary_invoice_group_day__date_invoices', [
	'date',
	'isCancelled',
	'hasSales',
	'fkCompanyId',
	'fkClientId',
	'fkUserId',
	'fkInvoiceId',
])
export class SalesSummaryInvoiceGroupDayEntity extends BaseEntity {
	/** The invoice date. */
	@Column('date', { name: 'date' })
	public date!: string;

	/** `invoice._id`. */
	@Column('int', { name: 'fk_invoice__id', unsigned: true })
	public fkInvoiceId!: number;

	/** `invoice._fk_client__id`. */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** `invoice.fk_user__id`, NULL and 0 kept apart. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	/** `invoice.fk_company__id`. */
	@Column('int', { name: 'fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	/** `invoice.type` as stored. */
	@Column('varchar', { name: 'type', length: 10, nullable: true })
	public type!: string | null;

	/** 1 when the invoice is cancelled (`invoice.status` = '4'). */
	@Column({ type: 'boolean', name: 'isCancelled', width: 1, default: () => "'0'" })
	public isCancelled!: boolean;

	/** 1 when the invoice counts as a sales invoice (`invoiceHasSalesSql`). */
	@Column({ type: 'boolean', name: 'hasSales', width: 1, default: () => "'0'" })
	public hasSales!: boolean;

	/** The order origin bucket of the lines, as in the line summary. */
	@Column('varchar', { name: 'originCode', length: 16 })
	public originCode!: string;

	/** 1 when the lines' item exists (an item join keeps them). */
	@Column({ type: 'boolean', name: 'hasItem', width: 1, default: () => "'0'" })
	public hasItem!: boolean;

	/** 1 when the lines' item is soft-deleted. */
	@Column({ type: 'boolean', name: 'isItemDeleted', width: 1, default: () => "'0'" })
	public isItemDeleted!: boolean;

	/** `item.isActive`, NULL for lines with no item. */
	@Column({ type: 'boolean', name: 'itemIsActive', width: 1, nullable: true })
	public itemIsActive!: boolean | null;

	/** `item.category`, as stored. */
	@Column('varchar', { name: 'category', length: 64, nullable: true })
	public category!: string | null;

	/** `item.customFields.productGroupCode`, unquoted, as stored. */
	@Column('varchar', { name: 'productGroupCode', length: 255, nullable: true })
	public productGroupCode!: string | null;

	/** `item.manufacturer`, as stored. */
	@Column('varchar', { name: 'manufacturer', length: 255, nullable: true })
	public manufacturer!: string | null;

	/** `item.brandEquivalent`, as stored. */
	@Column('varchar', { name: 'brandEquivalent', length: 255, nullable: true })
	public brandEquivalent!: string | null;

	/** `item.family`, as stored. */
	@Column('varchar', { name: 'family', length: 255, nullable: true })
	public family!: string | null;

	/** The lines of the invoice in this combination. */
	@Column('int', { name: 'lineCount', unsigned: true })
	public lineCount!: number;
}
